import { FirebaseFirestoreTypes } from "@react-native-firebase/firestore";
import { isEmpty } from "lodash";
import { useMemo } from "react";
import { Button, HStack, Stack } from "native-base";
import { useAreas } from "../../../hooks/useAreas";
import { useCracks } from "../../../hooks/useCracks";
import { ObjectType } from "../../../hooks/useObjectType";
import Subsection from "../../typo/Subsection";
import DisplayAreas from "./DisplayAreas";
import PrefillAreas from "./PrefillAreas";
import {
  AreaExpansionProvider,
  useAreaExpansion,
} from "./AreaExpansionContext";

type Props = {
  object: FirebaseFirestoreTypes.DocumentSnapshot<FirebaseFirestoreTypes.DocumentData>;
  updateObject: (data: any) => Promise<void>;
};

export default function Areas({ object, updateObject }: Props) {
  const projectId = object.ref.parent.parent!.id;
  const objectId = object.id;

  const {
    areas,
    addArea,
    rootAreas,
    levelForAreaId,
    isLoading: isLoadingAreas,
  } = useAreas(projectId, objectId);

  const [, cracksByAreaId, , isLoadingCracks] = useCracks(projectId, objectId);

  const collapsibleIds = useMemo(() => {
    const parentIds = new Set<string>();
    for (const area of areas) {
      const parentId = area.get<string | null>("parentId");
      if (parentId) parentIds.add(parentId);
    }
    return Array.from(parentIds);
  }, [areas]);

  if (isLoadingCracks || isLoadingAreas || !object) {
    return null;
  }

  const objectType = object.get<ObjectType | null>("objectType");

  return (
    <AreaExpansionProvider collapsibleIds={collapsibleIds}>
      <HStack alignItems="center" justifyContent="space-between" mb={2}>
        <Subsection title="Bereiche" />
        <ToggleAllAreasButton />
      </HStack>
      <Stack space={8}>
        <DisplayAreas
          objectType={objectType}
          projectId={projectId}
          objectId={objectId}
          areas={rootAreas}
          allAreas={areas}
          levelForAreaId={levelForAreaId}
          parentId={null}
          addArea={addArea}
          cracksByAreaId={cracksByAreaId}
          isLoadingCracks={isLoadingCracks}
        />

        {isEmpty(areas) && (
          <PrefillAreas object={object} updateObject={updateObject} />
        )}
      </Stack>
    </AreaExpansionProvider>
  );
}

function ToggleAllAreasButton() {
  const { anyCollapsed, hasCollapsibleAreas, expandAll, collapseAll } =
    useAreaExpansion();

  if (!hasCollapsibleAreas) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      onPress={anyCollapsed ? expandAll : collapseAll}
    >
      {anyCollapsed ? "Alle ausklappen" : "Alle einklappen"}
    </Button>
  );
}
